import { VStack, HStack, Button, Input, Box, Text } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { FaClock } from "react-icons/fa6"
import { useState } from "react"
import { getDatePreset } from "../../utils/reporting/dateHelpers"

interface DateRangeFilterProps {
  startDate: string
  endDate: string
  includeFuture: boolean
  onDateRangeChange: (start: string, end: string) => void
  onIncludeFutureChange: (value: boolean) => void
  includeScheduledLabel?: string
}

export default function DateRangeFilter({
  startDate,
  endDate,
  includeFuture,
  onDateRangeChange,
  onIncludeFutureChange,
  includeScheduledLabel = "Include Scheduled",
}: DateRangeFilterProps) {
  const [localStartDate, setLocalStartDate] = useState(startDate)
  const [localEndDate, setLocalEndDate] = useState(endDate)
  // Set financial-year as default active preset since it's the default date range
  const [activePreset, setActivePreset] = useState<string | null>("financial-year")

  const handlePresetClick = (preset: "last-7" | "last-30" | "next-30" | "current-month" | "financial-year") => {
    const dates = getDatePreset(preset)
    setLocalStartDate(dates.start)
    setLocalEndDate(dates.end)
    setActivePreset(preset)
    onDateRangeChange(dates.start, dates.end)
  }

  const handleStartDateChange = (value: string) => {
    setLocalStartDate(value)
    setActivePreset(null) // Clear preset when manually changing dates
    if (value && localEndDate && value <= localEndDate) {
      onDateRangeChange(value, localEndDate)
    }
  }

  const handleEndDateChange = (value: string) => {
    setLocalEndDate(value)
    setActivePreset(null) // Clear preset when manually changing dates
    if (localStartDate && value && localStartDate <= value) {
      onDateRangeChange(localStartDate, value)
    }
  }

  const isValidDateRange = localStartDate && localEndDate && localStartDate <= localEndDate

  return (
    <VStack align="stretch" gap={4} width={"100%"}>
      {/* Row 1: Date Controls */}
      <HStack minWidth={"150px"} maxWidth={{ base: "100%", lg: "600px" }} flex={{ base: "1 1 100%", lg: "0 1 auto" }}>
        <Field label="Start Date">
          <Input type="date" value={localStartDate} onChange={(e) => handleStartDateChange(e.target.value)} />
        </Field>

        <Box height="100%" display="flex" alignItems="center" justifyContent="center" pt={7}>
          <Text>to</Text>
        </Box>
        <Field label="End Date">
          <Input type="date" value={localEndDate} onChange={(e) => handleEndDateChange(e.target.value)} />
        </Field>
      </HStack>

      {!isValidDateRange && localStartDate && localEndDate && (
        <Text fontSize="xs" color="red.500">
          Start date must be before or equal to end date
        </Text>
      )}

      {/* Row 2: Date Range Presets and Scheduled Toggle */}
      <HStack justify="space-between" align="center" wrap={{ base: "wrap", md: "nowrap" }} gap={4}>
        <HStack gap={2} wrap="wrap">
          <Button
            size="sm"
            variant={(activePreset === "last-7" ? "active" : "hydrologiq_secondary") as any}
            onClick={() => handlePresetClick("last-7")}
          >
            Last 7 days
          </Button>
          <Button
            size="sm"
            variant={(activePreset === "last-30" ? "active" : "hydrologiq_secondary") as any}
            onClick={() => handlePresetClick("last-30")}
          >
            Last 30 days
          </Button>
          <Button
            size="sm"
            variant={(activePreset === "next-30" ? "active" : "hydrologiq_secondary") as any}
            onClick={() => handlePresetClick("next-30")}
          >
            Next 30 days
          </Button>
          <Button
            size="sm"
            variant={(activePreset === "current-month" ? "active" : "hydrologiq_secondary") as any}
            onClick={() => handlePresetClick("current-month")}
          >
            This Month
          </Button>
          <Button
            size="sm"
            variant={(activePreset === "financial-year" ? "active" : "hydrologiq_secondary") as any}
            onClick={() => handlePresetClick("financial-year")}
          >
            Financial Year
          </Button>
        </HStack>

        {/* <HStack gap={2} flex={{ base: "1 1 100%", md: "0 0 auto" }} justify={{ base: "flex-start", md: "flex-end" }}>
          <FaClock style={{ color: "var(--chakra-colors-fg-muted)", minWidth: "16px" }} />
          <Switch checked={includeFuture} onCheckedChange={(e) => onIncludeFutureChange(e.checked)}>
            {includeScheduledLabel}
          </Switch>
        </HStack> */}
      </HStack>
    </VStack>
  )
}
